import type { ColumnDef } from '@tanstack/react-table';

import { DataTable, textColumn, booleanColumn } from 'components/DataTable';
import { WalletFragmentFragment } from 'data/__generated__';
import { getPeriodLabel } from 'pages/Subventions/constants/states';

type SubventionRow = WalletFragmentFragment['subventions'][number];

const columns: ColumnDef<SubventionRow, unknown>[] = [
    textColumn<SubventionRow, Record<string, unknown>>({
        accessorKey: 'name',
        header: 'Nom',
        defaultVisible: true,
    }),
    textColumn<SubventionRow, Record<string, unknown>>({
        id: 'amount',
        accessorFn: (row) => {
            if (row.currentAmount === null || row.currentAmount === undefined) {
                return 'illimité';
            }
            return `${row.currentAmount.toFixed(2)} €${row.reload ? ' / ' + getPeriodLabel(row.reload) : ''}`;
        },
        header: 'Montant',
        defaultVisible: true,
    }),
    textColumn<SubventionRow, Record<string, unknown>>({
        id: 'remaining',
        accessorFn: (row) =>
            row.remainingAmount === null || row.remainingAmount === undefined
                ? '-'
                : `${row.remainingAmount.toFixed(2)} €`,
        header: 'Restant',
        defaultVisible: true,
    }),
    booleanColumn<SubventionRow, Record<string, unknown>>({
        id: 'cumulative',
        accessorFn: (row) => !!row.cumulative,
        header: 'Cumulable',
        defaultVisible: true,
    }),
    textColumn<SubventionRow, Record<string, unknown>>({
        id: 'categories',
        accessorFn: (row) => (row.categories[0] === '*' ? 'Tous produits' : `${row.categories.length} catégorie(s)`),
        header: 'Catégories',
        defaultVisible: true,
    }),
    textColumn<SubventionRow, Record<string, unknown>>({
        id: 'hours',
        accessorFn: (row) =>
            row.hoursIntervalsAvailable.length
                ? row.hoursIntervalsAvailable.map((interval) => interval.join('-')).join(', ')
                : 'Toujours',
        header: 'Horaires',
        defaultVisible: true,
    }),
    textColumn<SubventionRow, Record<string, unknown>>({
        id: 'organization',
        accessorFn: (row) => row.organization?.name || '-',
        header: 'Organisation',
        defaultVisible: true,
    }),
];

export function Subventions({ subventions }: { subventions: WalletFragmentFragment['subventions'] }) {
    return (
        <DataTable<SubventionRow>
            columns={columns}
            data={subventions}
            filterMode="client"
            getRowId={(row) => row._id}
            getRowLink={(row) => `/subvention/${row._id}`}
            disableUrlSync
            autoFocusSearch={false}
        />
    );
}
